import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Box,
  Avatar,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Fade,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import axios from "axios";
import { useState, useEffect } from "react";
import { hostname } from "../../utils";

interface WhitelabelInstance {
  id: string;
  domain?: string;
  settings: any;
  is_owner: boolean;
  created_date?: string;
  updated_date?: string;
}

interface OwnerInfo {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export default function WhitelabelOwnersDialog({
  open,
  onClose,
  getToken,
  whitelabel,
  eventListener,
}: {
  open: boolean;
  onClose: () => void;
  getToken: Function;
  whitelabel: WhitelabelInstance | null;
  eventListener?: Function;
}) {
  const [owners, setOwners] = useState<OwnerInfo[]>([]);
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingOwner, setAddingOwner] = useState(false);
  const [removingOwner, setRemovingOwner] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  useEffect(() => {
    if (open) {
      loadOwners();
    }
  }, [open]);

  const loadOwners = async () => {
    if (!whitelabel) return;

    setLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/whitelabel/${whitelabel.id}/owners`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setOwners(response.data.owners || []);
    } catch (error) {
      console.error("Error loading owners:", error);
      showSnackbar("Failed to load owners", "error");
    } finally {
      setLoading(false);
    }
  };

  const addOwner = async () => {
    if (!newOwnerEmail.trim()) {
      showSnackbar("Please enter an email address", "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newOwnerEmail.trim())) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    if (!whitelabel) return;

    setAddingOwner(true);
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/owners`,
        { owner_email: newOwnerEmail.trim() },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Reload owners to get the full owner info (including uid and displayName)
      await loadOwners();
      setNewOwnerEmail("");
      showSnackbar("Owner added successfully", "success");
    } catch (error: any) {
      console.error("Error adding owner:", error);
      const errorMessage = error?.response?.data?.error || "Failed to add owner";
      showSnackbar(errorMessage, "error");
    } finally {
      setAddingOwner(false);
    }
  };

  const removeOwner = async (owner: OwnerInfo) => {
    if (!whitelabel) return;

    const ownerIdentifier = owner.email || owner.uid;
    setRemovingOwner(ownerIdentifier);
    try {
      const currentToken = await getToken();
      await axios.delete(`${hostname}/whitelabel/${whitelabel.id}/owners/${encodeURIComponent(owner.uid)}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setOwners(owners.filter(o => o.uid !== owner.uid));
      showSnackbar("Owner removed successfully", "success");
    } catch (error: any) {
      console.error("Error removing owner:", error);
      const errorMessage = error?.response?.data?.error || "Failed to remove owner";
      showSnackbar(errorMessage, "error");
    } finally {
      setRemovingOwner(null);
    }
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    // Use global eventListener for errors so they appear on top of everything
    if (eventListener && severity === "error") {
      eventListener({ type: "SET_ERROR", error: message });
    } else if (eventListener && severity === "success") {
      eventListener({ type: "SET_SUCCESS", message: message });
    } else {
      // Fallback to local snackbar if eventListener not provided
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    }
  };

  // Helper function to generate avatar from owner info
  const getAvatarProps = (owner: OwnerInfo) => {
    let initials = "?";
    let name = owner.displayName || owner.email || owner.uid;

    if (owner.displayName) {
      // Use display name for initials
      const nameParts = owner.displayName.split(' ');
      initials = nameParts
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    } else if (owner.email) {
      // Use email for initials
      const emailName = owner.email.split('@')[0];
      initials = emailName
        .split('.')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    } else {
      // Use first 2 chars of UID
      initials = owner.uid.substring(0, 2).toUpperCase();
    }

    // Generate a consistent color based on uid
    const colors = [
      '#1976d2', '#388e3c', '#f57c00', '#d32f2f',
      '#7b1fa2', '#0097a7', '#5d4037', '#616161'
    ];
    const colorIndex = owner.uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

    return {
      children: initials,
      sx: { bgcolor: colors[colorIndex], width: 40, height: 40 }
    };
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !addingOwner && newOwnerEmail.trim()) {
      addOwner();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon />
            <Box>
              <Typography variant="h6" component="div">
                Manage Owners
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {whitelabel?.settings?.title || whitelabel?.domain || "Whitelabel Instance"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            icon={<EmailIcon />}
          >
            <Typography variant="body2">
              <strong>Share whitelabel access</strong> by adding email addresses.
              Owners can manage settings, branding, and add other owners.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Note: Users must already have an account on the platform before they can be added as an owner.
            </Typography>
          </Alert>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Add New Owner
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Email Address"
                value={newOwnerEmail}
                onChange={(e) => setNewOwnerEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                fullWidth
                placeholder="colleague@company.com"
                type="email"
                variant="outlined"
                size="small"
                disabled={addingOwner}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <Button
                variant="contained"
                onClick={addOwner}
                disabled={addingOwner || !newOwnerEmail.trim()}
                startIcon={addingOwner ? <CircularProgress size={16} /> : <PersonAddIcon />}
                sx={{
                  minWidth: 80,
                  height: 40
                }}
              >
                {addingOwner ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </Paper>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Current Owners ({owners.length + 1})
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {/* Current user as primary owner */}
                <Fade in={true}>
                  <Paper elevation={0} sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}>
                    <ListItem sx={{ py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              You (Primary Owner)
                            </Typography>
                            <Chip
                              label="Owner"
                              size="small"
                              color="primary"
                              icon={<AdminPanelSettingsIcon />}
                            />
                          </Box>
                        }
                        secondary="Full administrative access"
                      />
                    </ListItem>
                  </Paper>
                </Fade>

                {owners.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No additional owners yet
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Add email addresses above to share access
                    </Typography>
                  </Paper>
                ) : (
                  owners.map((owner, index) => {
                    const displayText = owner.displayName || owner.email || owner.uid;
                    const ownerIdentifier = owner.email || owner.uid;
                    return (
                      <Fade in={true} key={owner.uid} style={{ transitionDelay: `${index * 100}ms` }}>
                        <Paper elevation={0} sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}>
                          <ListItem sx={{ py: 2 }}>
                            <ListItemAvatar>
                              <Avatar {...getAvatarProps(owner)} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {displayText}
                                  </Typography>
                                  <Chip
                                    label="Shared Owner"
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                  />
                                </Box>
                              }
                              secondary={owner.email && owner.displayName ? owner.email : "Can manage whitelabel settings"}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => removeOwner(owner)}
                                disabled={removingOwner === ownerIdentifier}
                                color="error"
                                sx={{
                                  '&:hover': {
                                    bgcolor: 'error.lighter',
                                    color: 'error.main'
                                  }
                                }}
                              >
                                {removingOwner === ownerIdentifier ?
                                  <CircularProgress size={20} /> :
                                  <DeleteIcon />
                                }
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </Paper>
                      </Fade>
                    );
                  })
                )}
              </List>
            )}
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
